import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ChangePasswordModal } from './ChangePasswordModal'
import type { CurrentUser } from '../types'
import { api, stopPreview } from '../api'
import { initials } from '../format'
import { useTheme, THEME_LABELS, type ThemeChoice } from '../theme'
import { Icon, type IconName } from './Icon'
import { isActive, navModel, useNavGo, type NavGroup, type NavItem } from './nav'
import { Sheet } from './Sheet'
import { CommandPalette } from './CommandPalette'
import { Segmented } from '../finance/parts'

const THEME_ICONS: Record<ThemeChoice, IconName> = { auto: 'monitor', light: 'sun', dark: 'moon' }

/** Нижняя панель телефона: до четырёх главных пунктов из выданных и «Ещё». */
function tabsOf(groups: NavGroup[]): Array<{ key: string; label: string; icon: IconName; item: NavItem; group: NavGroup }> {
  const pick = (key: NavGroup['key'], label: string, icon: IconName) => {
    const group = groups.find((item) => item.key === key)
    return group && group.items[0] ? [{ key, label, icon, item: group.items[0], group }] : []
  }
  const tabs = [
    ...pick('finance', 'Финансы', 'overview'),
    ...pick('sales', 'Продажи', 'user'),
    ...pick('stock', 'Склад', 'stock'),
    ...pick('reports', 'Отчёты', 'reports'),
  ]
  // Без финансов первой вкладкой становится сводка: главная должна быть внизу всегда.
  if (!tabs.some((tab) => tab.key === 'finance')) tabs.unshift(...pick('summary', 'Сводка', 'summary'))
  // У подрядчика 1С нет ни отчётов, ни сводки — его главная работа в «Данных».
  if (tabs.length === 0) tabs.push(...pick('data', 'Данные', 'data'))
  return tabs.slice(0, 4)
}

function ThemeSwitch({ theme, setTheme }: { theme: ThemeChoice; setTheme: (choice: ThemeChoice) => void }) {
  return (
    <div className="menu__theme">
      <span className="menu__label" id="theme-label">
        Тема
      </span>
      <Segmented
        size="sm"
        label="Тема оформления"
        value={theme}
        onChange={setTheme}
        // «Как в системе» не помещается третью ширины меню профиля: в узком
        // месте — «Авто».
        options={(['auto', 'light', 'dark'] as ThemeChoice[]).map((value) => ({
          value,
          label: value === 'auto' ? 'Авто' : THEME_LABELS[value],
        }))}
      />
    </div>
  )
}

export function Layout({
  user,
  onLogout,
  title,
  subtitle,
  actions,
  documentTitle,
  children,
}: {
  user: CurrentUser
  onLogout: () => void
  title: ReactNode
  subtitle?: ReactNode
  actions?: ReactNode
  /** Заголовок вкладки, когда title — не строка. */
  documentTitle?: string
  children: ReactNode
}) {
  const navigate = useNavigate()
  const [theme, setTheme] = useTheme()
  const [menuOpen, setMenuOpen] = useState(false)
  const [passwordOpen, setPasswordOpen] = useState(false)
  const [navOpen, setNavOpen] = useState(false)
  const location = useLocation()
  const go = useNavGo()
  const groups = navModel(user)
  const tabs = tabsOf(groups)
  // Подсказка для полосы иконок (1024–1180): название пункта справа от иконки.
  const [tip, setTip] = useState<{ label: string; top: number } | null>(null)
  const showTip = (label: string, element: HTMLElement) => {
    const rect = element.getBoundingClientRect()
    setTip({ label, top: rect.top + rect.height / 2 })
  }
  const [moreOpen, setMoreOpen] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)
  // Куда вернуть фокус после окна смены пароля: пункт меню к этому времени закрыт.
  const userChip = useRef<HTMLButtonElement>(null)
  const moreButton = useRef<HTMLButtonElement>(null)
  const passwordFrom = useRef<'menu' | 'more'>('menu')

  // ⌘K / Ctrl+K — командная палитра из любого места.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setPaletteOpen((value) => !value)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])
  const moreActive =
    !tabs.some((tab) => tab.group.items.some((item) => isActive(item, location.pathname))) &&
    groups.some((group) => group.items.some((item) => isActive(item, location.pathname)))

  // На телефоне меню закрывается после перехода: иначе новая страница
  // открывается под развёрнутым списком.
  useEffect(() => {
    setNavOpen(false)
    setMoreOpen(false)
  }, [location.pathname])

  // Заголовок вкладки: с несколькими открытыми отчётами без него
  // невозможно понять, где что.
  useEffect(() => {
    const text = documentTitle ?? (typeof title === 'string' ? title : 'Сводка')
    document.title = `${text} · Сводка`
  }, [title, documentTitle])

  const logout = async () => {
    await api.logout().catch(() => undefined)
    onLogout()
    navigate('/login')
  }

  const open = (item: NavItem) => (event: React.MouseEvent) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey) return
    event.preventDefault()
    go(item)
  }

  return (
    <div className={`app${user.preview ? ' app--preview' : ''}`}>
      {user.preview && (
        <div className="mode-bar" role="status">
          <Icon name="eye" />
          <span className="mode-bar__text">
            Вы смотрите глазами: <strong>{user.full_name}</strong>
            {user.roles.length > 0 && <span className="mode-bar__roles"> · {user.roles.map((role) => role.title).join(', ')}</span>}
            <span className="mode-bar__note"> · запись и админка в этом режиме не работают</span>
          </span>
          <button
            type="button"
            className="btn btn--sm mode-bar__exit"
            onClick={() => {
              const target = user.id
              stopPreview()
              window.location.assign(`/admin/users/${target}`)
            }}
          >
            Выйти из просмотра
          </button>
        </div>
      )}

      <div className="shell">
        <aside className={`sidebar${navOpen ? ' sidebar--open' : ''}`}>
          <div className="sidebar__brand">
            <div className="sidebar__mark" aria-hidden="true">
              С
            </div>
            <div className="sidebar__names">
              <div className="sidebar__title">Сводка</div>
              <div className="sidebar__subtitle">Управленческая отчётность</div>
            </div>
            <button
              type="button"
              className="sidebar__toggle"
              aria-expanded={navOpen}
              aria-controls="sidebar-nav"
              onClick={() => setNavOpen((value) => !value)}
            >
              <span className="sidebar__toggle-bars" aria-hidden="true" />
              <span className="sr-only">Меню</span>
            </button>
          </div>

          <nav className="sidebar__nav" id="sidebar-nav" aria-label="Разделы">
            {groups.map((group, groupIndex) => (
              <div key={group.key} className="nav-group">
                <div className={`nav-section${groupIndex === 0 ? ' nav-section--first' : ''}`}>{group.title}</div>
                {group.items.map((item) => {
                  const active = isActive(item, location.pathname)
                  return (
                    <a
                      key={item.key}
                      href={item.path}
                      className={`nav-item${active ? ' nav-item--active' : ''}`}
                      aria-current={active ? 'page' : undefined}
                      onClick={open(item)}
                      onMouseEnter={(event) => showTip(item.label, event.currentTarget)}
                      onMouseLeave={() => setTip(null)}
                      onFocus={(event) => showTip(item.label, event.currentTarget)}
                      onBlur={() => setTip(null)}
                    >
                      {active && <span className="nav-item__pill" aria-hidden="true" />}
                      <Icon name={item.icon} className="nav-item__icon" />
                      <span className="nav-item__label truncate">{item.label}</span>
                    </a>
                  )
                })}
              </div>
            ))}
          </nav>

          <div className="sidebar__footer">
            {/* Клик по своему имени открывает меню, а не выходит из системы:
                случайный выход посреди работы — потерянные фильтры и разрез. */}
            <button ref={userChip} className="user-chip" onClick={() => setMenuOpen((value) => !value)} aria-expanded={menuOpen} aria-haspopup="menu">
              <span className="avatar">{initials(user.full_name)}</span>
              <span className="user-chip__text">
                <span className="user-chip__name">{user.full_name}</span>
                <span className="user-chip__role">{user.is_admin ? 'Администратор' : (user.roles[0]?.title ?? 'Без роли')}</span>
              </span>
              <Icon name="more" className="user-chip__more" />
            </button>

            {menuOpen && (
              <>
                <div className="menu-scrim" onClick={() => setMenuOpen(false)} />
                <div className="menu" role="menu">
                  <div className="menu__header">
                    <div className="small strong truncate">{user.email}</div>
                    <div className="tiny muted">
                      {user.is_admin
                        ? 'Полный доступ, включая настройку прав'
                        : user.roles.map((role) => role.title).join(', ') || 'Роли не назначены'}
                    </div>
                  </div>

                  <ThemeSwitch theme={theme} setTheme={setTheme} />

                  <button
                    className="menu__item"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false)
                      passwordFrom.current = 'menu'
                      setPasswordOpen(true)
                    }}
                  >
                    <Icon name="key" /> Сменить пароль
                  </button>

                  <button className="menu__item menu__item--danger" role="menuitem" onClick={logout}>
                    <Icon name="logout" /> Выйти
                  </button>
                </div>
              </>
            )}
          </div>
        </aside>

        <div className="main">
          <header className="topbar">
            <div className="topbar__titles">
              {typeof title === 'string' ? <h1>{title}</h1> : title}
              {subtitle && <div className="topbar__subtitle">{subtitle}</div>}
            </div>
            {actions}
            <button
              type="button"
              className="btn btn--ghost topbar__search"
              onClick={() => setPaletteOpen(true)}
              aria-label="Быстрый переход: разделы, отчёты, период (Ctrl+K)"
              title="Быстрый переход (Ctrl+K)"
            >
              <Icon name="search" />
              <span className="topbar__search-text">Поиск</span>
              <kbd className="palette__kbd topbar__search-kbd">{navigator.platform.includes('Mac') ? '⌘K' : 'Ctrl K'}</kbd>
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--icon topbar__theme"
              onClick={() => setTheme(theme === 'auto' ? 'light' : theme === 'light' ? 'dark' : 'auto')}
              aria-label={`Тема: ${THEME_LABELS[theme].toLowerCase()}. Нажмите, чтобы сменить`}
              title={`Тема: ${THEME_LABELS[theme].toLowerCase()}`}
            >
              <Icon name={THEME_ICONS[theme]} />
            </button>
          </header>

          <main className="content">
            {subtitle && <div className="content__subtitle">{subtitle}</div>}
            {children}
          </main>
        </div>
      </div>

      {tip && (
        <div className="rail-tip" style={{ top: tip.top }} aria-hidden="true">
          {tip.label}
        </div>
      )}

      <nav className="tabbar" aria-label="Главные разделы">
        {tabs.map((tab) => {
          const active = tab.group.items.some((item) => isActive(item, location.pathname))
          return (
            <a
              key={tab.key}
              href={tab.item.path}
              className={`tabbar__item${active ? ' tabbar__item--active' : ''}`}
              aria-current={active ? 'page' : undefined}
              onClick={open(tab.item)}
            >
              <Icon name={tab.icon} size={20} />
              <span>{tab.label}</span>
            </a>
          )
        })}
        <button
          ref={moreButton}
          type="button"
          className={`tabbar__item${moreActive ? ' tabbar__item--active' : ''}`}
          onClick={() => setMoreOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={moreOpen}
        >
          <Icon name="menu" size={20} />
          <span>Ещё</span>
        </button>
      </nav>

      <Sheet open={moreOpen} onClose={() => setMoreOpen(false)} title="Все разделы">
        <div className="more">
          {groups.map((group) => (
            <section key={group.key} className="more__group">
              <h3 className="more__title">{group.title}</h3>
              {group.items.map((item) => {
                const active = isActive(item, location.pathname)
                return (
                  <a
                    key={item.key}
                    href={item.path}
                    className={`more__item${active ? ' more__item--active' : ''}`}
                    aria-current={active ? 'page' : undefined}
                    onClick={(event) => {
                      setMoreOpen(false)
                      open(item)(event)
                    }}
                  >
                    <Icon name={item.icon} />
                    <span>{item.label}</span>
                  </a>
                )
              })}
            </section>
          ))}
          <section className="more__group">
            <h3 className="more__title">{user.full_name}</h3>
            <div className="more__note">{user.email}</div>
            <ThemeSwitch theme={theme} setTheme={setTheme} />
            <button
              type="button"
              className="more__item"
              onClick={() => {
                setMoreOpen(false)
                passwordFrom.current = 'more'
                setPasswordOpen(true)
              }}
            >
              <Icon name="key" /> <span>Сменить пароль</span>
            </button>
            <button type="button" className="more__item more__item--danger" onClick={logout}>
              <Icon name="logout" /> <span>Выйти</span>
            </button>
          </section>
        </div>
      </Sheet>

      <CommandPalette user={user} open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      {passwordOpen && (
        <ChangePasswordModal
          onClose={() => setPasswordOpen(false)}
          returnFocus={() => (passwordFrom.current === 'more' ? moreButton.current : userChip.current)}
        />
      )}
    </div>
  )
}
